import { FileDown, FilePlus, FileText, Printer as PrinterIcon, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import { EetStatusBadge } from '@/components/Eet/EetStatusBadge'
import { KeyValueList, Money, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { EetSubmission } from '@invo.space/shared'
import { Receipt } from '@invo.space/shared'
import API from '@/lib/api'
import { Printer } from '@/lib/printer/printer'
import { revalidateStockItems } from '@/lib/warehouse/stockAvailability'
import { Role } from '@/types/User'

import {
  formatDateTime,
  invoiceReference,
  lineLabel,
  lineTotalMinor,
  printReceiptOnUsb,
  receiptCurrency,
  receiptPaymentLabel,
  receiptPermalink,
  receiptSymbol,
  receiptTaxFree,
  receiptTotal,
} from './receiptUtils'

interface ReceiptSheetProps {
  receipt: Receipt | null
  eetSubmission?: EetSubmission
  printer?: Printer | null
  onOpenChange: (open: boolean) => void
  onDeleted?: () => void
}

/** Detail účtenky v pravém panelu nad seznamem (stejný jazyk jako DocumentSheet). */
export const ReceiptSheet = ({ receipt, eetSubmission, printer, onOpenChange, onDeleted }: ReceiptSheetProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [printing, setPrinting] = useState(false)
  const canEdit = useRoleCheck([Role.owner, Role.manager, Role.employee])

  if (!receipt) return <Sheet open={false} onOpenChange={onOpenChange} />

  const currency = receiptCurrency(receipt)
  const total = receiptTotal(receipt)
  const taxFree = receiptTaxFree(receipt)
  const symbol = receiptSymbol(receipt)
  const invoice = invoiceReference(receipt)
  const permalink = receiptPermalink(receipt)

  const printUsb = async () => {
    if (!printer) return
    setPrinting(true)
    try {
      await printReceiptOnUsb(printer, receipt)
      toast.success('Účtenka odeslána na tiskárnu')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Tisk se nepodařil')
    } finally {
      setPrinting(false)
    }
  }

  const handleDelete = (): Promise<void> =>
    API.delete(`/api/v2/receipts/${receipt.id}`, { headers: { 'Content-Type': 'multipart/form-data' } }).then((response) => {
      if (response.status === 204) {
        revalidateStockItems()
        toast.success('Účtenka smazána')
        onOpenChange(false)
        onDeleted?.()
      } else {
        toast.error('Účtenku se nepodařilo smazat')
      }
    })

  return (
    <>
      <Sheet open={!!receipt} onOpenChange={onOpenChange}>
        <SheetContent className="overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="flex items-center gap-3 border-b px-6 py-4 pr-12">
            <SheetTitle className="text-sm font-medium tabular">Účtenka {symbol}</SheetTitle>
            <StatusBadge status="issued" label={receiptPaymentLabel[receipt.paymentType] ?? receipt.paymentType} />
          </div>
          <SheetDescription className="sr-only">Detail účtenky {symbol}</SheetDescription>

          <div className="space-y-6 px-6 py-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground tabular">{formatDateTime(receipt.issuedAt)}</p>
              <Money amount={total} currency={currency} size="xl" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button asChild>
                <a href={permalink} target="_blank" rel="noreferrer">
                  <FileDown />
                  Účtenka PDF
                </a>
              </Button>
              {printer?.isBrowserSupported() && (
                <Button variant="outline" onClick={printUsb} disabled={printing}>
                  <PrinterIcon />
                  {printing ? 'Tisknu' : 'Vytisknout na USB'}
                </Button>
              )}
              {invoice ? (
                <Button variant="outline" asChild>
                  <Link to={`/invoice/${invoice.target}`}>
                    <FileText />
                    Zobrazit fakturu
                  </Link>
                </Button>
              ) : (
                canEdit && (
                  <Button variant="outline" asChild>
                    <Link to={`/invoice/new?receiptId=${receipt.id}`}>
                      <FilePlus />
                      Vystavit fakturu
                    </Link>
                  </Button>
                )
              )}
            </div>

            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Položky</p>
              <ul className="divide-y border-y">
                {receipt.receiptLines.map((line) => {
                  const lineCurrency = line.unitPrice?.currency ?? currency
                  return (
                    <li key={line.id} className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="truncate">{lineLabel(line)}</p>
                        <p className="text-xs text-muted-foreground tabular">
                          {Number(line.amount).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} ×{' '}
                          <Money amount={line.unitPrice?.amount} currency={lineCurrency} size="sm" className="font-normal" />
                          {line.taxRate ? `, DPH ${line.taxRate} %` : ''}
                          {line.sale && line.sale.type === 'percent' && line.sale.value > 0 ? `, sleva ${line.sale.value} %` : ''}
                        </p>
                      </div>
                      <Money amount={lineTotalMinor(line)} currency={lineCurrency} size="sm" />
                    </li>
                  )
                })}
              </ul>
            </div>

            <KeyValueList
              items={[
                { label: 'Číslo účtenky', value: symbol },
                { label: 'Vystaveno', value: formatDateTime(receipt.issuedAt) },
                { label: 'Způsob platby', value: receiptPaymentLabel[receipt.paymentType] ?? receipt.paymentType },
                { label: 'Bez DPH', value: <Money amount={taxFree} currency={currency} size="sm" /> },
                { label: 'DPH', value: <Money amount={total - taxFree} currency={currency} size="sm" /> },
                ...(receipt.supplier?.ico ? [{ label: 'IČO', value: receipt.supplier.ico }] : []),
                ...(eetSubmission
                  ? [
                      { label: 'EET 2.0', value: <EetStatusBadge submission={eetSubmission} showPok={false} /> },
                      ...(eetSubmission.pok ? [{ label: 'POK', value: <span className="font-mono text-xs">{eetSubmission.pok}</span> }] : []),
                    ]
                  : []),
              ]}
            />

            {canEdit && (
              <Button variant="ghost" className="text-danger hover:bg-danger-bg hover:text-danger" onClick={() => setConfirmOpen(true)}>
                <Trash2 />
                Smazat účtenku
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        isOpen={confirmOpen}
        setIsOpen={setConfirmOpen}
        title={`Smazat účtenku ${symbol}?`}
        description="Opravdu chcete tuto účtenku smazat? Akci nejde vrátit."
        onConfirm={handleDelete}
      />
    </>
  )
}
